import { i18n } from '@/base';
import Base from '@/Base.vue';
import Vue from 'vue';
import router from './router';
import store from './store';
import 'iframe-resizer/js/iframeResizer.contentWindow.js';
import { OpenTicketShop } from '@openticket/lib-shop';
import { Logger } from '@openticket/lib-sdk-helpers';
import VueError from '@/error';
import { registerIframeResizerListener } from '@/utils/isIframeResizer';
import { IFramePageOptions } from 'iframe-resizer';
import { CookieManager } from '@/composables/cookies';
import { useWaas } from '@/composables/waas';

declare module 'vue/types/vue' {
    interface Vue {
        $cookies: CookieManager;
        $shop: OpenTicketShop;
        $logger: Logger;
    }
}

interface QueueItEnvInfo {
    cid: string;

    [index: string]: unknown;
}

interface QueueIt {
    Tools: {
        EnvironmentHelper: {
            retrieveEnvInfoFromScriptTag(): QueueItEnvInfo;
        };
    };
    Javascript: {
        Version?: string;
        IntegrationConfig: {
            loadIntegrationConfigFromServer(): void;
        };
    };

    isIntegrationConfigReady(): boolean;
}

interface QueueItClient {
    options: {
        host: string;
    };
    customerId: string;
    eventId: string;
    cookieManager: {
        getQueueStateCookie(): string | null | undefined;
    };
}

declare global {
    interface Window {
        Shop: OpenTicketShop;
        iFrameResizer: IFramePageOptions & {
            /**
             * Receive message posted from the parent page with the iframe.iFrameResizer.sendMessage() method.
             */
            onMessage?(message: any): void;

            /**
             * This function is called once iFrame-Resizer has been initialized after receiving a call from the parent page.
             */
            onReady?(): void;
        };
        QueueIt: QueueIt;
        myQueueClient: QueueItClient;
        initialLocationHref: string;
    }
}

const sessionId =
    new URLSearchParams(window.location.search).get('sessionId') || undefined;

const logger = (Vue.prototype.$logger = new Logger('shop', {
    sessionId,
}));

window.initialLocationHref = window.location.href;

// Add shop SDK to vue prototype
const shop: OpenTicketShop = Vue.observable(
    new OpenTicketShop({
        sessionId: logger.sessionId,
    })
);
Vue.prototype.$shop = shop;
Object.defineProperty(window, 'Shop', {
    get: () => shop,
});

try {
    useWaas(shop.log);
} catch (e) {
    console.error('WAAS failed', { e });
}

new Vue({
    router,
    store,
    i18n,
    render: h => h(Base),
}).$mount('#app');

Vue.config.errorHandler = err => {
    if (err && '_isOpenTicketLogMessage' in err) {
        // These have already been logged with the current logger version.
        // With the new logger (@openticket/lib-log) this needs to be validated for each error.
        return;
    }

    logger.throw(new VueError('root', err || new Error('Unknown error')));
};

registerIframeResizerListener();

window.addEventListener('message', (event: MessageEvent) => {
    if (
        typeof event.data === 'string' &&
        event.data.startsWith('window.scrollY:')
    ) {
        document.documentElement.style.setProperty(
            '--ot-injector-iframe-parent-scroll-top',
            `${event.data.slice(15)}px`
        );
    }
});
